import { getCurrencyExponent } from './minor-units'

/**
 * Masks and formats an amount input string for currency display as the user types.
 * - Strips all characters except digits and one decimal point
 * - Zero-decimal currencies (JPY, KRW, etc.) disallow decimal input
 * - Formats the integer part with thousands separators (en-US locale)
 * - Limits decimal digits to the currency's ISO 4217 exponent (0, 2, or 3)
 * - Caps the numeric value at maxValue if provided
 *
 * @param {string} rawInput - The raw string from the input element
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {number} [maxValue] - Optional maximum numeric value
 * @returns {{ displayValue: string, numericValue: number|null }}
 */
export function maskAmountInput(rawInput, currencyCode, maxValue) {
    // Strip everything except digits and decimal point
    let cleaned = rawInput.replace(/[^\d.]/g, '')

    // Allow only one decimal point
    const firstDot = cleaned.indexOf('.')
    if (firstDot !== -1) {
        cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
    }

    const exponent = getCurrencyExponent(currencyCode)

    // Zero-decimal currencies: truncate at the decimal point (discard decimal part)
    if (exponent === 0) {
        const dotPos = cleaned.indexOf('.')
        if (dotPos !== -1) {
            cleaned = cleaned.slice(0, dotPos)
        }
    }

    if (!cleaned) {
        return { displayValue: '', numericValue: null }
    }

    // User just typed a bare decimal point — preserve it, no numeric value yet
    if (cleaned === '.') {
        return { displayValue: '.', numericValue: null }
    }

    const dotIndex = cleaned.indexOf('.')
    const hasDecimal = dotIndex !== -1
    const intStr = hasDecimal ? cleaned.slice(0, dotIndex) : cleaned
    const decStr = hasDecimal ? cleaned.slice(dotIndex + 1) : undefined

    // Format integer part with thousands separators
    const intNum = parseInt(intStr || '0', 10)
    const formattedInt = isNaN(intNum) ? '' : intNum.toLocaleString('en-US')

    // Build display value, preserving partial decimal input
    let displayValue
    if (decStr !== undefined) {
        const trimmedDec = decStr.slice(0, exponent)
        displayValue = formattedInt + '.' + trimmedDec
    } else {
        displayValue = formattedInt
    }

    let numericValue = parseFloat(cleaned)
    if (isNaN(numericValue)) numericValue = null

    // Apply max cap
    if (maxValue != null && numericValue != null && numericValue > maxValue) {
        numericValue = maxValue
        const cappedStr = exponent > 0 ? maxValue.toFixed(exponent) : String(Math.floor(maxValue))
        const [cInt, cDec] = cappedStr.split('.')
        const formattedCappedInt = parseInt(cInt, 10).toLocaleString('en-US')
        displayValue = cDec !== undefined ? formattedCappedInt + '.' + cDec : formattedCappedInt
    }

    return { displayValue, numericValue }
}

/**
 * Formats an existing numeric/string value for display in the amount input
 * (e.g. pre-filled values from URL params or locked params).
 *
 * @param {string|number} value - The value to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted display string
 */
export function formatAmountForDisplay(value, currencyCode) {
    if (value === '' || value === null || value === undefined) return ''
    const numericValue = parseFloat(String(value).replace(/,/g, ''))
    if (isNaN(numericValue)) return ''
    const exponent = getCurrencyExponent(currencyCode)
    const parts = numericValue.toFixed(exponent).split('.')
    const formattedInt = parseInt(parts[0], 10).toLocaleString('en-US')
    return parts[1] !== undefined ? formattedInt + '.' + parts[1] : formattedInt
}
